import React, { useState, useEffect, useMemo } from "react";
import ExcelTable4 from "../../Components/Tables/TableExcel";
import Encaissement from "../../Services/Encaissement";
import {
  MonetizationOn,
  AttachMoney,
  Category,
  AccountBalanceWallet,
  AddCircleOutline,
  CreateNewFolder,
  EventAvailable,
  Close,
  Save,
  AttachMoney as AttachMoneyIcon,
  DateRange as DateRangeIcon,
  AddCircle,
  TrendingUp,
  Search,
  CalendarToday,
  Receipt,
  MoreVert,
  Download,
  Print,
  Refresh,
} from "@mui/icons-material";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  TableContainer,
  Paper,
  InputAdornment,
  ListItemIcon,
  Collapse,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  useTheme,
  Avatar,
  Fade,
  Zoom,
  Chip,
  alpha,
  Menu,
  Tooltip,
  Badge,
  LinearProgress,
  Divider,
  Skeleton
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const EncaissementsPage = () => {
  const theme = useTheme();
  const [encaissements, setEncaissements] = useState([]);
  const [newEncaissement, setNewEncaissement] = useState({
    value: "",
    type: "",
    date: new Date().toISOString().split("T")[0],
    customType: "",
  });
  const [editingEncaissement, setEditingEncaissement] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchType, setSearchType] = useState("");
  const [dateDebut, setDateDebut] = useState("");
  const [dateFin, setDateFin] = useState("");
  const [error, setError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const restaurantId = localStorage.getItem("restaurantId");
  const openMenu = Boolean(anchorEl);

  // Define filteredEncaissements FIRST before using it in other useMemo calculations
  const filteredEncaissements = useMemo(() => {
    return encaissements.filter((encaissement) => {
      const typeMatch = searchType ? encaissement.type === searchType : true;
      const date = new Date(encaissement.date);
      const startDate = dateDebut ? new Date(dateDebut) : null;
      const endDate = dateFin ? new Date(dateFin) : null;
      const searchMatch = searchTerm 
        ? encaissement.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
          encaissement.value.toString().includes(searchTerm)
        : true;

      if (startDate) startDate.setUTCHours(0, 0, 0, 0);
      if (endDate) endDate.setUTCHours(23, 59, 59, 999);

      const dateMatch =
        (!startDate || date >= startDate) && (!endDate || date <= endDate);

      return typeMatch && dateMatch && searchMatch;
    });
  }, [encaissements, searchType, dateDebut, dateFin, searchTerm]);

  // Now use filteredEncaissements in other useMemo calculations
  const totalEncaissements = useMemo(() => {
    return filteredEncaissements.reduce((sum, enc) => sum + parseFloat(enc.value), 0);
  }, [filteredEncaissements]);

  const averageEncaissement = useMemo(() => {
    return filteredEncaissements.length > 0 
      ? (totalEncaissements / filteredEncaissements.length).toFixed(2) 
      : 0;
  }, [filteredEncaissements, totalEncaissements]);

  const uniqueTypes = useMemo(() => {
    return [...new Set(encaissements.map((e) => e.type))];
  }, [encaissements]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchedEncaissements =
          await Encaissement.getEncaissementsByRestaurantId(restaurantId);
        setEncaissements(fetchedEncaissements);
      } catch (error) {
        setError("Failed to load transactions. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) fetchData();
  }, [restaurantId]);

  const handleAddEncaissement = async () => {
    const { value, type, date, customType } = newEncaissement;
    const finalType = type === "Autre" ? customType : type;

    if (!value || !finalType || !date) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      const encaissement = new Encaissement(
        parseFloat(value),
        finalType,
        date,
        restaurantId
      );
      await encaissement.addEncaissement();
      setEncaissements((prev) => [...prev, encaissement]);
      handleCloseDialog();
    } catch (error) {
      setError("Failed to create transaction. Please try again.");
    }
  };

  const handleEditEncaissement = (enc) => {
    const isCustomType = !uniqueTypes.includes(enc.type);

    setEditingEncaissement(enc);
    setNewEncaissement({
      value: parseFloat(enc.value),
      type: isCustomType ? "Autre" : enc.type,
      date: enc.date,
      customType: isCustomType ? enc.type : "",
    });
    setOpenDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const updates = {
        value: parseFloat(newEncaissement.value),
        type:
          newEncaissement.type === "Autre"
            ? newEncaissement.customType
            : newEncaissement.type,
        date: newEncaissement.date,
      };

      await Encaissement.updateEncaissement(editingEncaissement.id, updates);
      setEncaissements((prev) =>
        prev.map((enc) =>
          enc.id === editingEncaissement.id ? { ...enc, ...updates } : enc
        )
      );
      handleCloseDialog();
    } catch (error) {
      setError("Échec de la mise à jour de la transaction. Veuillez réessayer.");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEncaissement(null);
    setNewEncaissement({
      value: "",
      type: "",
      date: new Date().toISOString().split("T")[0],
      customType: "",
    });
    setError("");
  };

  const handleDeleteConfirmation = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteEncaissement = async () => {
    try {
      await Encaissement.deleteEncaissement(deleteId);
      setEncaissements((prev) =>
        prev.filter((encaissement) => encaissement.id !== deleteId)
      );
      setDeleteDialogOpen(false);
      setDeleteId(null);
    } catch (error) {
      console.error("Error deleting encaissement:", error.message);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const clearFilters = () => {
    setSearchType("");
    setDateDebut("");
    setDateFin("");
    setSearchTerm("");
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box sx={{ p: 4, background: '#F5F7FA', minHeight: '100vh' }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 4, mb: 4, bgcolor: '#E0E0E0' }} />
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1,2,3,4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3, bgcolor: '#E0E0E0' }} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 3, bgcolor: '#E0E0E0' }} />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        p: 4, 
        background: '#F5F7FA',
        minHeight: '100vh',
        position: 'relative',
      }}
    >
      {/* Header Section with Stats */}
      <motion.div
        initial="initial"
        animate="animate"
        variants={staggerContainer}
      >
        {/* Hero Card */}
        <motion.div variants={fadeInUp}>
          <Card
            sx={{
              mb: 4,
              background: 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
              borderRadius: 4,
              border: '1px solid #E0E0E0',
              overflow: 'visible',
              position: 'relative',
            }}
          >
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
                        boxShadow: '0 10px 20px rgba(46,125,50,0.2)',
                      }}
                    >
                      <AttachMoneyIcon sx={{ fontSize: 48, color: '#FFFFFF' }} />
                    </Avatar>
                  </Zoom>
                  <Box>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontWeight: 800,
                        color: '#1A2B3C',
                        mb: 1,
                      }}
                    >
                      Gestion des Encaissements
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#5F6B7A', fontWeight: 400 }}>
                      Suivi et gestion en temps réel des transactions financières
                    </Typography>
                  </Box>
                </Box>
                <Badge
                  badgeContent={filteredEncaissements.length}
                  color="success"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '1rem',
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
                    }
                  }}
                >
                  <Receipt sx={{ fontSize: 50, color: '#B0BEC5' }} />
                </Badge>
              </Box>
            </CardContent>
          </Card>
        </motion.div>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[
            { 
              label: 'Total Encaissements', 
              value: `${totalEncaissements.toLocaleString()} TND`, 
              icon: <AttachMoney />, 
              color: '#2E7D32',
              bgColor: '#E8F5E9',
              delay: 0.1
            },
            { 
              label: 'Nombre de Transactions', 
              value: filteredEncaissements.length, 
              icon: <Receipt />, 
              color: '#1976D2',
              bgColor: '#E3F2FD',
              delay: 0.2
            },
            { 
              label: 'Moyenne par Transaction', 
              value: `${averageEncaissement} TND`, 
              icon: <TrendingUp />, 
              color: '#ED6C02',
              bgColor: '#FFF3E0',
              delay: 0.3
            },
            { 
              label: 'Types de Transactions', 
              value: uniqueTypes.length, 
              icon: <Category />, 
              color: '#9C27B0',
              bgColor: '#F3E5F5',
              delay: 0.4
            },
          ].map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                variants={fadeInUp}
                transition={{ delay: stat.delay }}
              >
                <Card
                  sx={{
                    background: '#FFFFFF',
                    borderRadius: 3,
                    border: '1px solid #E0E0E0',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 20px 30px ${alpha(stat.color, 0.1)}`,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#5F6B7A', textTransform: 'uppercase', letterSpacing: 1 }}>
                          {stat.label}
                        </Typography>
                        <Typography variant="h5" sx={{ color: '#1A2B3C', fontWeight: 700, mt: 1 }}>
                          {stat.value}
                        </Typography>
                      </Box>
                      <Avatar
                        sx={{
                          bgcolor: stat.bgColor,
                          color: stat.color,
                          width: 56,
                          height: 56,
                        }}
                      >
                        {stat.icon}
                      </Avatar>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        {/* Filters Section */}
        <motion.div variants={fadeInUp}>
          <Card
            sx={{
              mb: 4,
              background: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid #E0E0E0',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search sx={{ color: '#9AA6B2' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <FormControl fullWidth variant="outlined">
                    <Select
                      value={searchType}
                      onChange={(e) => setSearchType(e.target.value)}
                      displayEmpty
                      sx={{
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        color: '#1A2B3C',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                      }}
                    >
                      <MenuItem value="">Tous les types</MenuItem>
                      {uniqueTypes.map((type) => (
                        <MenuItem key={type} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => setDateDebut(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#9AA6B2', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    value={dateFin}
                    onChange={(e) => setDateFin(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarToday sx={{ color: '#9AA6B2', fontSize: 18 }} />
                        </InputAdornment>
                      ),
                      sx: {
                        backgroundColor: '#F5F7FA',
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#E0E0E0',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#2E7D32',
                        },
                      }
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={() => setOpenDialog(true)}
                      sx={{
                        height: 56,
                        background: 'linear-gradient(45deg, #2E7D32, #4CAF50)',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&:hover': {
                          background: 'linear-gradient(45deg, #4CAF50, #2E7D32)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 10px 20px rgba(46,125,50,0.2)',
                        },
                      }}
                    >
                      <AddCircle sx={{ mr: 1 }} />
                      Nouveau
                    </Button>
                    <Tooltip title="Réinitialiser les filtres">
                      <IconButton
                        onClick={clearFilters}
                        sx={{
                          backgroundColor: '#F5F7FA',
                          borderRadius: 2,
                          width: 56,
                          height: 56,
                          border: '1px solid #E0E0E0',
                          '&:hover': {
                            borderColor: '#2E7D32',
                            backgroundColor: '#E8F5E9',
                          },
                        }}
                      >
                        <Refresh sx={{ color: '#5F6B7A' }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>

              {/* Active Filters */}
              {(searchType || dateDebut || dateFin || searchTerm) && (
                <Fade in={true}>
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {searchType && (
                      <Chip
                        label={`Type: ${searchType}`}
                        onDelete={() => setSearchType('')}
                        sx={{
                          backgroundColor: '#E8F5E9',
                          color: '#2E7D32',
                          border: '1px solid #A5D6A7',
                        }}
                      />
                    )}
                    {dateDebut && (
                      <Chip
                        label={`Du: ${dateDebut}`}
                        onDelete={() => setDateDebut('')}
                        sx={{
                          backgroundColor: '#E3F2FD',
                          color: '#1976D2',
                          border: '1px solid #90CAF9',
                        }}
                      />
                    )}
                    {dateFin && (
                      <Chip
                        label={`Au: ${dateFin}`}
                        onDelete={() => setDateFin('')}
                        sx={{
                          backgroundColor: '#FFF3E0',
                          color: '#ED6C02',
                          border: '1px solid #FFB74D',
                        }}
                      />
                    )}
                    {searchTerm && (
                      <Chip
                        label={`Recherche: ${searchTerm}`}
                        onDelete={() => setSearchTerm('')}
                        sx={{
                          backgroundColor: '#F3E5F5',
                          color: '#9C27B0',
                          border: '1px solid #CE93D8',
                        }}
                      />
                    )}
                  </Box>
                </Fade>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Excel Table Section */}
        <motion.div variants={fadeInUp}>
          <Card
            sx={{
              mb: 4,
              background: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid #E0E0E0',
              overflow: 'hidden',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ color: '#1A2B3C', fontWeight: 600 }}>
                  Aperçu Excel
                </Typography>
                <Box>
                  <Tooltip title="Télécharger">
                    <IconButton sx={{ color: '#5F6B7A', '&:hover': { color: '#2E7D32' } }}>
                      <Download />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Imprimer">
                    <IconButton sx={{ color: '#5F6B7A', '&:hover': { color: '#2E7D32' } }}>
                      <Print />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
              <div style={{ overflowX: "auto" }}>
                <ExcelTable4 data={filteredEncaissements} title="Encaissements" />
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Transactions Table */}
        <motion.div variants={fadeInUp}>
          <Card
            sx={{
              background: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid #E0E0E0',
              overflow: 'hidden',
            }}
          >
            {loading && <LinearProgress sx={{ background: 'linear-gradient(90deg, #2E7D32, #4CAF50)' }} />}
            
            <TableContainer>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
                    {["Date", "Valeur", "Type", "Actions"].map((header, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          fontWeight: 600,
                          fontSize: "0.9rem",
                          color: "#1A2B3C",
                          borderBottom: '2px solid #E0E0E0',
                          py: 2,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <AnimatePresence>
                    {filteredEncaissements
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((enc, index) => (
                        <motion.tr
                          key={enc.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ delay: index * 0.05 }}
                          onMouseEnter={() => setHoveredRow(enc.id)}
                          onMouseLeave={() => setHoveredRow(null)}
                          style={{
                            backgroundColor: hoveredRow === enc.id 
                              ? '#F5F7FA'
                              : index % 2 === 0 
                                ? '#FFFFFF' 
                                : '#FAFAFA',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                          }}
                        >
                          <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <CalendarToday sx={{ fontSize: 16, color: '#9AA6B2' }} />
                              <Typography sx={{ color: '#1A2B3C' }}>{enc.date}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                            <Typography
                              sx={{
                                color: '#2E7D32',
                                fontWeight: 700,
                                fontSize: '1.1rem',
                              }}
                            >
                              {enc.value} TND
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                            <Chip
                              label={enc.type}
                              size="small"
                              sx={{
                                backgroundColor: '#E8F5E9',
                                color: '#2E7D32',
                                border: '1px solid #A5D6A7',
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <Tooltip title="Modifier">
                                <IconButton
                                  onClick={() => handleEditEncaissement(enc)}
                                  sx={{
                                    color: '#5F6B7A',
                                    '&:hover': { 
                                      color: '#2E7D32',
                                      backgroundColor: '#E8F5E9',
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  onClick={() => handleDeleteConfirmation(enc.id)}
                                  sx={{
                                    color: '#5F6B7A',
                                    '&:hover': { 
                                      color: '#D32F2F',
                                      backgroundColor: '#FFEBEE',
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Plus d'options">
                                <IconButton
                                  onClick={(e) => handleMenuClick(e, enc)}
                                  sx={{
                                    color: '#5F6B7A',
                                    '&:hover': { 
                                      color: '#9C27B0',
                                      backgroundColor: '#F3E5F5',
                                    },
                                  }}
                                >
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </motion.tr>
                      ))}
                  </AnimatePresence>
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredEncaissements.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid #E0E0E0',
                color: '#1A2B3C',
                '& .MuiTablePagination-select': {
                  color: '#1A2B3C',
                },
                '& .MuiTablePagination-selectIcon': {
                  color: '#1A2B3C',
                },
              }}
            />
          </Card>
        </motion.div>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: "100%",
            maxWidth: 500,
            background: '#FFFFFF',
            border: '1px solid #E0E0E0',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #D32F2F 0%, #F44336 100%)',
            color: "#FFFFFF",
            py: 3,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <DeleteIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="600">
                Confirmer la suppression
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Cette action est irréversible
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Typography variant="body1" sx={{ color: '#5F6B7A' }}>
            Êtes-vous sûr de vouloir supprimer cet encaissement ?
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E0E0E0' }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: '#BDBDBD',
              color: '#5F6B7A',
              '&:hover': {
                borderColor: '#9E9E9E',
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleDeleteEncaissement}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(45deg, #D32F2F 0%, #F44336 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #F44336 0%, #D32F2F 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(211,47,47,0.2)',
              },
            }}
          >
            Confirmer la suppression
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            borderRadius: 4,
            width: '100%',
            maxWidth: 600,
            background: '#FFFFFF',
            border: '1px solid #E0E0E0',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
          color: "#FFFFFF",
          py: 3,
        }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <MonetizationOn sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h5" fontWeight="600">
                {editingEncaissement ? "Modifier l'Encaissement" : "Nouvel Encaissement"}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {editingEncaissement ? "Mettre à jour les informations" : "Ajouter une nouvelle transaction"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
                <br></br>
              <TextField
                fullWidth
                label="Montant (TND)"
                type="number"
                variant="outlined"
                value={newEncaissement.value}
                onChange={(e) => setNewEncaissement({ ...newEncaissement, value: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney sx={{ color: '#2E7D32' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: '#F5F7FA',
                    borderRadius: 2,
                    color: '#1A2B3C',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E0E0E0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2E7D32',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2E7D32',
                    },
                  }
                }}
                InputLabelProps={{
                  sx: { color: '#5F6B7A' }
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel sx={{ color: '#5F6B7A' }}>Type de Transaction</InputLabel>
                <Select
                  value={newEncaissement.type}
                  onChange={(e) => setNewEncaissement({
                    ...newEncaissement,
                    type: e.target.value,
                    customType: e.target.value === "Autre" ? newEncaissement.customType : "",
                  })}
                  label="Type de Transaction"
                  sx={{
                    backgroundColor: '#F5F7FA',
                    borderRadius: 2,
                    color: '#1A2B3C',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E0E0E0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2E7D32',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2E7D32',
                    },
                  }}
                >
                  <MenuItem value="" disabled>
                    <ListItemIcon>
                      <Category sx={{ color: '#9AA6B2' }} />
                    </ListItemIcon>
                    Sélectionnez le type
                  </MenuItem>
                  {uniqueTypes.map((type) => (
                    <MenuItem key={type} value={type} sx={{ color: '#1A2B3C' }}>
                      <ListItemIcon>
                        <AccountBalanceWallet sx={{ color: '#2E7D32' }} />
                      </ListItemIcon>
                      {type}
                    </MenuItem>
                  ))}
                  <MenuItem value="Autre" sx={{ color: '#1A2B3C' }}>
                    <ListItemIcon>
                      <AddCircleOutline sx={{ color: '#4CAF50' }} />
                    </ListItemIcon>
                    Type personnalisé
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Collapse in={newEncaissement.type === "Autre"} timeout={300} sx={{ width: '100%' }}>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Nom du type personnalisé"
                  variant="outlined"
                  value={newEncaissement.customType}
                  onChange={(e) => setNewEncaissement({ ...newEncaissement, customType: e.target.value.toUpperCase() })}
                  InputProps={{
                    sx: {
                      backgroundColor: '#F5F7FA',
                      borderRadius: 2,
                      color: '#1A2B3C',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E0E0E0',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2E7D32',
                      },
                    }
                  }}
                  InputLabelProps={{
                    sx: { color: '#5F6B7A' }
                  }}
                />
              </Grid>
            </Collapse>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Date de transaction"
                type="date"
                variant="outlined"
                value={newEncaissement.date}
                onChange={(e) => setNewEncaissement({ ...newEncaissement, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EventAvailable sx={{ color: '#2E7D32' }} />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: '#F5F7FA',
                    borderRadius: 2,
                    color: '#1A2B3C',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E0E0E0',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#2E7D32',
                    },
                  }
                }}
                InputLabelProps={{
                  sx: { color: '#5F6B7A' }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #E0E0E0' }}>
          <Button 
            onClick={handleCloseDialog} 
            variant="outlined"
            startIcon={<Close />}
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: '#BDBDBD',
              color: '#5F6B7A',
              '&:hover': {
                borderColor: '#9E9E9E',
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Annuler
          </Button>
          <Button 
            onClick={editingEncaissement ? handleSaveEdit : handleAddEncaissement}
            variant="contained"
            startIcon={editingEncaissement ? <Save /> : <AddCircle />}
            sx={{
              borderRadius: 2,
              px: 3,
              background: 'linear-gradient(45deg, #2E7D32 0%, #4CAF50 100%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #4CAF50 0%, #2E7D32 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 20px rgba(46,125,50,0.2)',
              },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            {editingEncaissement ? "Mettre à jour" : "Créer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Options Menu */}
      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            background: '#FFFFFF',
            border: '1px solid #E0E0E0',
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          }
        }}
      >
        <MenuItem onClick={() => {
          handleEditEncaissement(selectedRow);
          handleMenuClose();
        }} sx={{ color: '#1A2B3C', '&:hover': { backgroundColor: '#E8F5E9' } }}>
          <ListItemIcon><EditIcon sx={{ color: '#2E7D32' }} /></ListItemIcon>
          Modifier
        </MenuItem>
        <MenuItem onClick={() => {
          handleDeleteConfirmation(selectedRow?.id);
          handleMenuClose();
        }} sx={{ color: '#1A2B3C', '&:hover': { backgroundColor: '#FFEBEE' } }}>
          <ListItemIcon><DeleteIcon sx={{ color: '#D32F2F' }} /></ListItemIcon>
          Supprimer
        </MenuItem>
        <Divider sx={{ borderColor: '#E0E0E0' }} />
        <MenuItem onClick={handleMenuClose} sx={{ color: '#5F6B7A' }}>
          <ListItemIcon><Close sx={{ color: '#5F6B7A' }} /></ListItemIcon>
          Fermer
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default EncaissementsPage;